// @owners { team: patients-team }
import { createQueryKeys } from '@lukemorales/query-key-factory';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { fetchPaymentMethodsSucceeded } from '~shared/actions/paymentMethods';
import getIsAuthorized from '~shared/features/auth/selectors/getIsAuthorized';
import { get } from '~shared/helpers/apiHelper';
import { getErrorMessageFromResponse } from '~shared/helpers/helper';
import { staleTimes } from '~shared/queries/constants';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { type PaymentMethod, type ReduxDispatchShared } from '~shared/types';

export const paymentMethods = createQueryKeys('paymentMethods', {
  fetchAll: ({ dispatch }: { dispatch: ReduxDispatchShared }) => ({
    queryKey: ['fetch-all-payment-methods'],
    queryFn: async (): Promise<PaymentMethod[]> => {
      const response = await get('/payment_methods');
      if (response.error) {
        throw new Error(getErrorMessageFromResponse(response));
      }
      // TODO: payment method is in used in selector getExistingOrder.
      // Keep redux state in sync until usage is removed
      dispatch(fetchPaymentMethodsSucceeded(response));
      return response;
    },
  }),
  fetchOne: ({ paymentMethodID }: { paymentMethodID: number }) => ({
    queryKey: ['fetchOne', paymentMethodID],
    queryFn: async (): Promise<PaymentMethod> => {
      const response = await get(`/payment_methods/${paymentMethodID}`);
      if (response.error) {
        throw new Error(getErrorMessageFromResponse(response));
      }

      return response;
    },
  }),
});

/**
 * Fetch all payment methods for a patient.
 * Runs only when authorized; `options.enabled` is AND-ed with auth (defaults to true when omitted).
 * @returns Array of payment methods
 */
export const useGetPaymentMethods = (options?: { enabled?: boolean }) => {
  const isAuthorized = useSelectorShared(getIsAuthorized);
  const enabled = isAuthorized && (options?.enabled ?? true);
  const dispatch = useDispatchShared();
  const { data, isLoading, isSuccess, error, refetch } = useQuery({
    ...paymentMethods.fetchAll({ dispatch }),
    enabled,
    staleTime: staleTimes.fifteenMinutes,
    gcTime: staleTimes.fifteenMinutes,
  });

  return {
    error,
    isLoading,
    isSuccess,
    paymentMethods: data ?? [],
    refetch,
  };
};

/**
 * Fetch ACH payment methods for a patient
 * @returns Array of payment methods
 */
export const useGetAchPaymentMethods = () => {
  const dispatch = useDispatchShared();
  const getACHPaymentMethods = useCallback((methods: PaymentMethod[]) => {
    const paymentMethods = methods || [];
    return paymentMethods.filter((method) => method.type === 'PlaidData');
  }, []);

  const { data, isLoading, isSuccess, error } = useQuery({
    ...paymentMethods.fetchAll({ dispatch }),
    select: getACHPaymentMethods,
    staleTime: staleTimes.fifteenMinutes,
    gcTime: staleTimes.fifteenMinutes,
  });

  return {
    error,
    isLoading,
    isSuccess,
    achPaymentMethods: data ?? [],
  };
};

const usePaymentMethodByIDFromCache = (paymentMethodID: number | undefined | null) => {
  const queryClient = useQueryClient();
  const dispatch = useDispatchShared();
  const cachedPaymentMethods: PaymentMethod[] =
    queryClient.getQueryData(paymentMethods.fetchAll({ dispatch }).queryKey) ?? [];
  return cachedPaymentMethods.find((pm) => pm.id === paymentMethodID);
};

/**
 * Fetch payment method for a patient
 * @returns payment method
 */
export const useGetPaymentMethod = ({ paymentMethodID, enabled }: { paymentMethodID: number; enabled: boolean }) => {
  const cachedPaymentMethod = usePaymentMethodByIDFromCache(paymentMethodID);
  const {
    data: paymentMethod,
    isLoading,
    isSuccess,
    error,
  } = useQuery({
    ...paymentMethods.fetchOne({ paymentMethodID }),
    enabled: !cachedPaymentMethod && enabled,
  });

  return {
    error,
    isLoading,
    isSuccess,
    paymentMethod,
  };
};
